import { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import ContributionsBadge from './feedback/ContributionsBadge';

const AvatarMenu = ({ profile, onSwitchPlan, onLogout, isDarkTheme }) => {
  const [showEarlyAccessNotice, setShowEarlyAccessNotice] = useState(false);
  const userInitial = String(profile.fullName || profile.email || '?').trim().slice(0, 1).toUpperCase();
  const userDisplayName = profile.fullName || profile.email;
  const isAdmin = profile.role === 'admin';

  const handlePlanChange = (plan) => {
    const { changed } = onSwitchPlan?.(plan) || {};
    if (changed && plan === 'PLUS') {
      setShowEarlyAccessNotice(true);
    }
  };

  const baseButton = 'w-full text-left px-4 py-2 rounded-lg transition-colors';

  return (
    <div
      className={clsx(
        'absolute right-0 mt-2 w-72 shadow-xl rounded-2xl border p-4 z-50',
        isDarkTheme ? 'bg-greenbook-dark-bg border-gray-800' : 'bg-white border-gray-200'
      )}
    >
      <div className="flex items-start space-x-3 pb-3 border-b border-gray-200 dark:border-gray-800">
        <div className="h-10 w-10 rounded-full bg-greenbook-light-primary/20 dark:bg-greenbook-dark-primary/20 flex items-center justify-center font-bold text-green-900 dark:text-green-100">
          {userInitial}
        </div>
        <div className="flex-1">
          <p className="font-semibold text-sm">{userDisplayName}</p>
          {profile.email && <p className="text-xs text-gray-500 dark:text-gray-400">{profile.email}</p>}
          <p className="text-xs mt-1 font-medium">
            Plano: <span className="uppercase">{profile.plan}</span> {profile.plan === 'PLUS' && '· Early Access'}
          </p>
          <div className="mt-2">
            <ContributionsBadge
              count={profile.contributionCount || 0}
              plusEntitlementUntil={profile.plusEntitlementUntil}
              plusLifetime={profile.plusLifetime}
              proTrialUntil={profile.proTrialUntil}
              isDarkTheme={isDarkTheme}
            />
          </div>
        </div>
      </div>

      <div className="space-y-2 py-3">
        <div className="flex items-center justify-between text-sm">
          <span>Free</span>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only"
              checked={profile.plan === 'PLUS'}
              onChange={(event) => handlePlanChange(event.target.checked ? 'PLUS' : 'FREE')}
            />
            <span
              className={clsx(
                'w-11 h-6 flex items-center bg-gray-300 rounded-full p-1 transition-colors',
                profile.plan === 'PLUS' ? 'bg-green-600' : isDarkTheme ? 'bg-gray-700' : 'bg-gray-300'
              )}
            >
              <span
                className={clsx(
                  'bg-white w-4 h-4 rounded-full shadow transform transition-transform',
                  profile.plan === 'PLUS' ? 'translate-x-5' : 'translate-x-0'
                )}
              />
            </span>
          </label>
          <span>Plus</span>
        </div>
        {showEarlyAccessNotice && (
          <p className="text-xs text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-2">
            Você está ativando recursos em Early Access que serão migrados para um plano pago em breve.
          </p>
        )}
      </div>

      <div className="space-y-2 border-t pt-3 border-gray-200 dark:border-gray-800">
        <Link
          to="/configuracoes"
          className={clsx(baseButton, "block", isDarkTheme ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50')}
        >
          Configurações
        </Link>
        <Link
          to="/minhas-contribuicoes"
          className={clsx(baseButton, "block", isDarkTheme ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50')}
        >
          Minhas contribuições
        </Link>
        {isAdmin && (
          <Link
            to="/admin/feedback"
            className={clsx(baseButton, "block", isDarkTheme ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50')}
          >
            Moderar feedbacks
          </Link>
        )}
        <button
          type="button"
          className={clsx(baseButton, isDarkTheme ? 'text-red-200 hover:bg-red-900/30' : 'text-red-700 hover:bg-red-50')}
          onClick={onLogout}
        >
          Sair
        </button>
      </div>
    </div>
  );
};

AvatarMenu.propTypes = {
  profile: PropTypes.shape({
    fullName: PropTypes.string,
    email: PropTypes.string,
    plan: PropTypes.string,
    role: PropTypes.string,
    contributionCount: PropTypes.number,
    plusEntitlementUntil: PropTypes.string,
    plusLifetime: PropTypes.bool,
    proTrialUntil: PropTypes.string,
  }).isRequired,
  onSwitchPlan: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  isDarkTheme: PropTypes.bool.isRequired,
};

export default AvatarMenu;
